import { useMemo } from "react";
import { taskDirtyCheck } from "./taskDirtyCheck";

import { scheduleDirtyCheck } from "./scheduleDirtyCheck";
import { sectionsDirtyCheck } from "./sectionsDirtyCheck";
import { graphql, useFragment } from "react-relay";
import { useRoutineEditDirty_Routine$key } from "./__generated__/useRoutineEditDirty_Routine.graphql";
import { getControlType } from "../RoutineEditMain/RoutineEditControlTypeCard/getControlType";
import { useRoutineEditState } from "../State/RoutineEditState";
import { connectionDirtyCheck } from "./connectionDirtyCheck";

export function useRoutineEditDirty(props: {
  routine: useRoutineEditDirty_Routine$key;
}) {
  const params = useRoutineEditState();
  const routine = useFragment(
    graphql`
      fragment useRoutineEditDirty_Routine on Routine {
        title
        description
        ownerTeam {
          id
        }
        assignedRoles {
          role {
            id
          }
        }
        activeVersion {
          tasks {
            id
            title
            instructions
            images {
              id
              size(minHeight: 700, minWidth: 700) {
                id
                url
                width
                height
              }
            }
            __typename
            ... on TaskCheck {
              sentiments
            }
            ... on TaskNumber {
              goals {
                greaterThan
                lessThan
                section {
                  id
                }
              }
              unit
            }
          }
          connections {
            taskId
            sectionId
            ordinal
            canBeDisabled
            orgTopic {
              id
              name
              isPrivate
            }
          }
          sections {
            orgCanBeDisabled
            orgTopic {
              id
            }
            section {
              id
              title
              assignedTeam {
                id
              }
            }
          }
        }
        currentSchedule {
          __typename
          ... on RoutineScheduleDaily {
            deadlineDuration
            isAllDay
            weekdays {
              startTime {
                hour
                minute
              }
              weekday
              tasks {
                importance
                taskId
              }
            }
          }
          ... on RoutineScheduleWeekly {
            startWeekday
            endWeekday
            weeks
          }
          ... on RoutineScheduleMonthly {
            months
            startDay
            endDay
          }
        }
      }
    `,
    props.routine
  );

  const remoteControlType = useMemo(
    () =>
      getControlType(
        routine.activeVersion?.tasks.map((t) => ({
          title: t.title,
          type: t.__typename,
          sentiments: t.sentiments,
        })) ?? []
      ),
    [routine]
  );

  return useMemo(() => {
    if (
      params.controlType !== undefined &&
      params.controlType !== remoteControlType
    ) {
      return true;
    }

    if (
      params.team !== undefined &&
      routine.ownerTeam &&
      params.team.id !== routine.ownerTeam.id
    ) {
      return true;
    }

    if (
      (params.title !== undefined &&
        params.title.trim() !== routine.title.trim()) ||
      (params.description !== undefined &&
        params.description.trim() !== (routine.description ?? "").trim()) ||
      params.addDescriptionFiles.length > 0 ||
      params.removeDescriptionFileIds.length > 0 ||
      params.addDescriptionMedia.length > 0 ||
      params.removeDescriptionMediaIds.length > 0
    ) {
      return true;
    }

    const localSchedule =
      params.scheduleFreq === "daily"
        ? params.scheduleDaily
        : params.scheduleFreq === "weekly"
          ? params.scheduleWeekly
          : params.scheduleFreq === "monthly"
            ? params.scheduleMonthly
            : undefined;

    if (
      scheduleDirtyCheck(
        routine.currentSchedule,
        localSchedule,
        params.tasks?.map((t) => t.id) ??
          routine.activeVersion.tasks.map((t) => t.id)
      )
    ) {
      return true;
    }

    if (sectionsDirtyCheck(routine.activeVersion.sections, params.sections)) {
      return true;
    }

    if (taskDirtyCheck(routine.activeVersion.tasks, params.tasks)) {
      return true;
    }

    if (
      connectionDirtyCheck(
        routine.activeVersion.connections,
        params.connections,
        routine.activeVersion.tasks
      )
    ) {
      return true;
    }

    // Check assigned roles
    if (params.assignedRoleIds !== undefined) {
      const remoteRoleIds = routine.assignedRoles
        .map((ar) => ar.role?.id)
        .filter((id): id is string => !!id)
        .sort();
      const localRoleIds = [...params.assignedRoleIds].sort();
      if (
        remoteRoleIds.length !== localRoleIds.length ||
        remoteRoleIds.some((id, i) => id !== localRoleIds[i])
      ) {
        return true;
      }
    }

    return false;
  }, [
    params.controlType,
    params.team,
    params.title,
    params.description,
    params.addDescriptionFiles.length,
    params.removeDescriptionFileIds.length,
    params.addDescriptionMedia.length,
    params.removeDescriptionMediaIds.length,
    params.scheduleFreq,
    params.scheduleDaily,
    params.scheduleWeekly,
    params.scheduleMonthly,
    params.tasks,
    params.sections,
    params.connections,
    params.assignedRoleIds,
    remoteControlType,
    routine.ownerTeam,
    routine.title,
    routine.description,
    routine.currentSchedule,
    routine.activeVersion.tasks,
    routine.activeVersion.sections,
    routine.activeVersion.connections,
    routine.assignedRoles,
  ]);
}
